import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@arthelper/shared';

const STORAGE_KEY = 'preferred-locale';

// Synchronously pre-cache the browser's preferred language before i18next initialises.
// i18next's own caching is async (runs after resource loading), so if the user gets
// redirected to town-square for auth before that completes, localStorage is never
// written and detection resets to English on return. Writing it here — synchronously,
// at module evaluation time — ensures the value survives the redirect.
if (!localStorage.getItem(STORAGE_KEY)) {
  const browserLangs = Array.from(navigator.languages ?? [navigator.language]);
  for (const lang of browserLangs) {
    const base = lang.split('-')[0];
    if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) {
      localStorage.setItem(STORAGE_KEY, base);
      break;
    }
  }
}

const catalogModules = import.meta.glob<{ default: Record<string, unknown> }>('../../packages/i18n/catalogs/**/*.json');

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(
    resourcesToBackend((language: string, namespace: string) => {
      const key = `../../packages/i18n/catalogs/${language}/${namespace}.json`;
      const loader = catalogModules[key];
      if (!loader) return Promise.reject(new Error(`Missing catalog: ${language}/${namespace}`));
      return loader();
    })
  )
  .init({
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    ns: [
      'common',
      'nav',
      'auth',
      'artwork',
      'community',
      'billing',
      'chat',
      'profile',
      'notifications',
      'settings',
      'social',
      'verify',
      'metadata',
      'admin',
      'onboarding',
      'reputation',
      'tutorials',
      'arty-card',
    ],
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'preferred-locale',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  });

export default i18n;
